/**
 * Shared utilities for event tagging functionality.
 * 
 * Tagging uses activity log notes with special prefixes:
 * - [TAG:eventId] - marks an event as tagged
 * - [UNTAG:eventId] - removes a tag from an event
 */

import { formatInTimeZone } from 'date-fns-tz';

// ============================================================================
// Types
// ============================================================================

export interface AlertNote {
  note?: string;
  date?: string | Date;
  author?: string;
}

export interface TaggedEventInfo {
  eventId: string;
  comment: string;
  imageUrl?: string;
  date: Date;
  author?: string;
  snapshotTime?: string;
}

export interface CreateTagNoteParams {
  eventId: string;
  eventTime: string;
  comment?: string;
  imageUrl?: string;
}

export interface CreateUntagNoteParams {
  eventId: string;
  eventTime: string;
}

// ============================================================================
// Constants (Regex patterns for matching tag prefixes)
// ============================================================================

/** Matches [TAG:uuid] prefix - captures the event ID */
export const TAG_PATTERN = /\[TAG:([a-f0-9-]+)\]/i;

/** Matches [UNTAG:uuid] prefix - captures the event ID */
export const UNTAG_PATTERN = /\[UNTAG:([a-f0-9-]+)\]/i;

/** Matches [TAG:uuid] prefix with everything after it */
export const TAG_WITH_CONTENT_PATTERN = /\[TAG:([a-f0-9-]+)\]\s*(.*?)(?:\n|$)/i;

/** Matches [TAG:uuid] or [UNTAG:uuid] for stripping from display */
export const TAG_OR_UNTAG_STRIP_PATTERN = /\[(?:UN)?TAG:[a-f0-9-]+\]\s*/gi;

/** Matches Image: URL lines for filtering */
export const IMAGE_LINE_PATTERN = /^Image:\s*https?:\/\//i;

// ============================================================================
// Note Creation Functions
// ============================================================================

/**
 * Creates a tag note text for adding to the activity log.
 * Format: [TAG:eventId] Added tag to {time} snapshot | Note: {comment}
 */
export function createTagNoteText({ eventId, eventTime, comment, imageUrl }: CreateTagNoteParams): string {
  const trimmedComment = comment?.trim();
  const baseText = `[TAG:${eventId}] Added tag to ${eventTime} snapshot${trimmedComment ? ` | Note: ${trimmedComment}` : ''}`;
  
  if (imageUrl) {
    return `${baseText}\n\nImage: ${imageUrl}`;
  }
  
  return baseText;
}

/**
 * Creates an untag note text for adding to the activity log.
 * Format: [UNTAG:eventId] Removed tag from {time} snapshot
 */
export function createUntagNoteText({ eventId, eventTime }: CreateUntagNoteParams): string {
  return `[UNTAG:${eventId}] Removed tag from ${eventTime} snapshot`;
}

/**
 * Formats an event time for display in tag notes.
 */
export function formatEventTimeForTag(occurred: string | Date | undefined, timeZone: string | undefined): string {
  if (!occurred || !timeZone) return 'Unknown time';
  return formatInTimeZone(new Date(occurred), timeZone, 'h:mm:ss a');
}

// ============================================================================
// Tag State Functions
// ============================================================================

/**
 * Derives the set of currently tagged event IDs from an array of notes.
 * Processes notes in chronological order (newest first) to determine current state.
 * An event is tagged if the most recent TAG/UNTAG note for it is a TAG.
 */
export function deriveTaggedEventIds(notes: AlertNote[] | undefined): Set<string> {
  const ids = new Set<string>();
  if (!notes) return ids;

  // Sort notes by date (newest first) to process in chronological order
  const sortedNotes = [...notes].sort(
    (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
  );

  // Track which events we've already determined the state for
  const processedEvents = new Set<string>();

  for (const note of sortedNotes) {
    if (!note.note) continue;

    // Check for TAG pattern
    const tagMatch = note.note.match(TAG_PATTERN);
    if (tagMatch && tagMatch[1] && !processedEvents.has(tagMatch[1])) {
      ids.add(tagMatch[1]);
      processedEvents.add(tagMatch[1]);
      continue;
    }

    // Check for UNTAG pattern
    const untagMatch = note.note.match(UNTAG_PATTERN);
    if (untagMatch && untagMatch[1] && !processedEvents.has(untagMatch[1])) {
      // Don't add to ids (it's untagged), but mark as processed
      processedEvents.add(untagMatch[1]);
    }
  }

  return ids;
}

/**
 * Checks if a specific event is currently tagged based on notes.
 * Returns true if the most recent TAG/UNTAG note for the event is a TAG.
 */
export function isEventTagged(eventId: string | undefined, notes: AlertNote[] | undefined): boolean {
  if (!eventId || !notes) return false;

  const tagPrefix = `[TAG:${eventId}]`;
  const untagPrefix = `[UNTAG:${eventId}]`;

  // Sort notes by date (newest first)
  const sortedNotes = [...notes].sort(
    (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
  );

  // Find the most recent tag or untag action for this event
  for (const note of sortedNotes) {
    if (note.note?.includes(tagPrefix)) return true;
    if (note.note?.includes(untagPrefix)) return false;
  }

  return false;
}

// ============================================================================
// Display Formatting Functions
// ============================================================================

/**
 * Formats note text for display by stripping TAG/UNTAG prefixes and image URLs.
 * Returns cleaned text suitable for showing to users.
 */
export function formatNoteTextForDisplay(noteText: string): string {
  // Strip [TAG:uuid] or [UNTAG:uuid] prefix
  let text = noteText.replace(TAG_OR_UNTAG_STRIP_PATTERN, '');

  // Remove lines that start with "Image:" followed by a URL
  text = text
    .split('\n')
    .filter((line) => !IMAGE_LINE_PATTERN.test(line))
    .join('\n')
    .trim();

  return text;
}

/**
 * Formats note text specifically for activity log display.
 * Similar to formatNoteTextForDisplay but may have different defaults.
 */
export function formatNoteForActivityLog(noteText: string): string {
  // Filter out lines with Google Cloud Storage URLs and Image: lines
  const lines = noteText.split('\n').filter((line) => {
    if (line.includes('https://storage.googleapis.com/')) return false;
    if (line.trim() === 'Image:') return false;
    if (IMAGE_LINE_PATTERN.test(line.trim())) return false;
    return true;
  });

  // Join remaining lines and strip TAG/UNTAG prefixes
  return lines.join('\n').replace(TAG_OR_UNTAG_STRIP_PATTERN, '').trim();
}

/**
 * Replaces TAG/UNTAG prefixes with user-friendly text for inline display.
 */
export function replaceTagPrefixesWithLabels(line: string): string {
  // Handle TAG prefix: strip the "[TAG:uuid] Added tag to X snapshot | Note: " boilerplate
  const tagMatch = line.match(/^\[TAG:[a-f0-9-]+\]\s*(.*)/i);
  if (tagMatch) {
    const rest = tagMatch[1];
    // Extract snapshot time and optional user note
    const snapshotMatch = rest.match(/Added tag to (.+?) snapshot/i);
    const noteMatch = rest.match(/\|\s*Note:\s*(.*)/i);
    const snapshotTime = snapshotMatch?.[1];
    const noteContent = noteMatch?.[1]?.trim();

    if (snapshotTime && noteContent) {
      return `Tagged ${snapshotTime} - Note: ${noteContent}`;
    }
    if (snapshotTime) {
      return `Tagged ${snapshotTime} snapshot`;
    }
    if (noteContent) {
      return `Tagged - Note: ${noteContent}`;
    }
    return rest ? `Tagged: ${rest}` : 'Tagged event';
  }

  // Handle UNTAG prefix: strip "[UNTAG:uuid] Removed tag from X snapshot" boilerplate
  const untagMatch = line.match(/^\[UNTAG:[a-f0-9-]+\]\s*(.*)/i);
  if (untagMatch) {
    const rest = untagMatch[1];
    const snapshotMatch = rest.match(/Removed tag from (.+?) snapshot/i);
    if (snapshotMatch) {
      return `Removed tag from ${snapshotMatch[1]} snapshot`;
    }
    return rest ? `Removed tag: ${rest}` : 'Removed tag from event';
  }

  return line
    .replace(/\[EVIDENCE_\d+\]\s*/g, 'Added Evidence Link: ')
    .replace(/\[EVIDENCE\]\s*/g, 'Added Evidence Link: ')
    .replace(/\[REMOVE_EVIDENCE_\d+\]\s*/g, '')
    .replace(/\[REMOVE_EVIDENCE:[^\]]+\]\s*/g, '');
}

// ============================================================================
// Tag Extraction Functions (for reports)
// ============================================================================

interface AlertEvent {
  id?: string;
  occurred?: string | Date;
}

/**
 * Extracts all tagged events from notes for use in reports.
 * Returns detailed information about each tagged event.
 */
export function extractTaggedEvents(
  notes: AlertNote[] | undefined,
  events: AlertEvent[] | undefined
): TaggedEventInfo[] {
  if (!notes || !events) return [];

  const tagMap = new Map<string, TaggedEventInfo>();

  // Sort notes by date ascending to process in order
  const sortedNotes = [...notes].sort(
    (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime()
  );

  for (const note of sortedNotes) {
    const noteText = note.note || '';

    // Check for TAG
    const tagMatch = noteText.match(TAG_WITH_CONTENT_PATTERN);
    if (tagMatch) {
      const eventId = tagMatch[1];
      const rawComment = tagMatch[2].trim();

      // Extract image URL if present
      const imageMatch = noteText.match(/Image:\s*(https:\/\/[^\s]+)/);
      const imageUrl = imageMatch?.[1];

      // Find the event to get its occurred time
      const event = events.find((e) => e.id === eventId);
      const snapshotTime = event?.occurred
        ? new Date(event.occurred).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          })
        : undefined;

      // Parse comment - extract the note if present (format: "Added tag to X snapshot | Note: comment")
      let comment = '';
      const noteMatch = rawComment.match(/\| Note:\s*(.*)/i);
      if (noteMatch) {
        comment = noteMatch[1].trim();
      }

      // If no comment, use snapshot time as the display text
      const displayComment = comment || (snapshotTime ? `${snapshotTime} snapshot` : 'Tagged for review');

      tagMap.set(eventId, {
        eventId,
        comment: displayComment,
        imageUrl,
        date: new Date(note.date || 0),
        author: note.author,
        snapshotTime,
      });
    }

    // Check for UNTAG - removes from map
    const untagMatch = noteText.match(UNTAG_PATTERN);
    if (untagMatch) {
      tagMap.delete(untagMatch[1]);
    }
  }

  return Array.from(tagMap.values());
}

// ============================================================================
// Specialized Formatting (for AdminAlertDetails)
// ============================================================================

/**
 * Formats note text specifically for the AdminAlertDetails activity log.
 * Handles TAG, UNTAG, EVIDENCE, and REMOVE_EVIDENCE prefixes.
 */
export function formatNoteForAdminDetails(noteText: string): string {
  // Skip evidence links - they're displayed separately (supports [EVIDENCE] and [EVIDENCE_N])
  if (/^\[EVIDENCE(?:_\d+)?\]/.test(noteText)) {
    return '';
  }

  // Skip evidence removal notes - they're displayed separately (supports [REMOVE_EVIDENCE:url] and [REMOVE_EVIDENCE_N])
  if (/^\[REMOVE_EVIDENCE[_:]/.test(noteText)) {
    return '';
  }

  // Skip report config notes - they're internal settings
  if (noteText.startsWith('[REPORT_CONFIG]')) {
    return '';
  }

  // Format start time override notes
  if (noteText.startsWith('[START_TIME_OVERRIDE]')) {
    const iso = noteText.replace('[START_TIME_OVERRIDE]', '').trim();
    if (iso === 'NONE') {
      return 'Incident start time removed';
    }
    const parsed = new Date(iso);
    const formatted = !isNaN(parsed.getTime())
      ? parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      : 'unknown time';
    return `Incident start time set to ${formatted}`;
  }

  // Strip [UNTAG:uuid] prefix and show the rest of the message
  const untagMatch = noteText.match(/\[UNTAG:[a-f0-9-]+\]\s*(.*)/is);
  if (untagMatch) {
    return (
      untagMatch[1]
        .split('\n')
        .filter((line) => !IMAGE_LINE_PATTERN.test(line))
        .join('\n')
        .trim() || 'Tag removed from event'
    );
  }

  // Strip [TAG:uuid] prefix and show the rest of the message
  const tagMatch = noteText.match(/\[TAG:[a-f0-9-]+\]\s*(.*)/is);
  if (tagMatch) {
    return (
      tagMatch[1]
        .split('\n')
        .filter((line) => !IMAGE_LINE_PATTERN.test(line))
        .join('\n')
        .trim() || 'Tagged for review'
    );
  }

  // Default: just remove image lines
  return noteText
    .split('\n')
    .filter((line) => !IMAGE_LINE_PATTERN.test(line))
    .join('\n')
    .trim();
}
